import { useEffect, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowUpRight, MapPin, Star, ShieldCheck, Sparkles, Instagram, Phone, MessageCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { useSeoHead } from "@/lib/head";
import { useSite } from "@/context/SiteContext";
import { spath } from "@/components/public/LanguageSwitcher";
import { ContentSections, FaqSection } from "@/components/public/PageParts";
import LeadForm from "@/components/public/LeadForm";
import Reveal from "@/components/public/Reveal";
import PreviewBanner from "@/components/public/PreviewBanner";
import ServicesShowcase from "@/components/public/ServicesShowcase";
import FaqAccordion from "@/components/public/FaqAccordion";
import OpenNowBadge from "@/components/public/OpenNowBadge";
import { HOURS_LABEL } from "@/lib/businessHours";
import { serviceImg, GALLERY, GALLERY_LABEL } from "@/lib/serviceImages";
import NotFound from "./NotFound";

const CONTACT_PHONE = "+90 542 878 64 99";
const CONTACT_WHATSAPP = "905428786499";

export default function Home() {
  const { lang } = useParams();
  const { setSwitcher } = useSite();
  const videoRef = useRef(null);
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const withPrev = (p) => (preview ? `${p}?preview=1` : p);

  const { data: page, isError } = useQuery({
    queryKey: ["home", lang, preview ? "prev" : "pub"],
    queryFn: async () => preview
      ? (await api.get(`/preview/page/${lang}`)).data
      : (await api.get(`/public/home/${lang}`)).data,
    retry: false,
  });
  const { data: nav } = useQuery({
    queryKey: ["nav", lang, preview ? "prev" : "pub"],
    queryFn: async () => preview
      ? (await api.get(`/preview/nav/${lang}`)).data
      : (await api.get(`/public/nav/${lang}`)).data,
  });

  useSeoHead(page);
  useEffect(() => { if (page?.switcher) setSwitcher(page.switcher); }, [page, setSwitcher]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  const services = nav?.services || [];
  const branches = nav?.branches || [];

  if (isError) return <NotFound />;
  const dirFor = (i) => (i % 3 === 0 ? "left" : i % 3 === 2 ? "right" : "up");

  return (
    <div className="overflow-x-hidden">
      {page?.preview && <PreviewBanner lang={lang} status={page.preview_meta?.status} updatedAt={page.preview_meta?.updated_at} />}
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-obsidian pt-28 pb-16 md:pt-32 md:pb-20">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-copper/10 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative z-10 container-x text-center">
          <p className="eyebrow mb-6 animate-fade-up flex items-center justify-center gap-3">
            <span className="inline-block w-8 h-px bg-copper accent-line" />
            Girne · Lefkoşa · Mağusa · KKTC
            <span className="inline-block w-8 h-px bg-copper accent-line" />
          </p>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.95] text-ink animate-fade-up max-w-4xl mx-auto" style={{ animationDelay: "0.06s" }}>
            {page?.content?.h1 || "Aracınızı Bir Üst Seviyeye Taşıyın"}
          </h1>
          <p className="text-subtle text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: "0.14s" }}>
            {page?.content?.intro}
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-9 animate-fade-up" style={{ animationDelay: "0.22s" }}>
            <a href="#quote" data-testid="hero-cta-quote" className="btn-copper text-base !px-8 !py-4">{t(lang, "getQuote")} <ArrowRight size={18} /></a>
            <a href="#services" className="btn-ghost text-base !px-8 !py-4">{t(lang, "heroCtaSecondary")}</a>
          </div>
        </div>

        {/* Media panel with floating badges */}
        <div className="relative z-10 container-x mt-14 animate-fade-up" style={{ animationDelay: "0.3s" }}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl h-[340px] sm:h-[440px] lg:h-[560px]">
            <video
              ref={videoRef}
              data-testid="hero-video"
              className="absolute inset-0 w-full h-full object-cover bg-obsidian"
              src="/media/hero.mp4"
              poster="/media/hero-poster.jpg"
              autoPlay muted loop playsInline preload="auto"
              aria-label={page?.seo?.image_alt || "Sıfır Bir Tuning"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-obsidian/20" />

            <div className="hidden sm:flex items-start gap-2.5 absolute top-5 left-5 max-w-[220px] rounded-xl bg-obsidian/70 backdrop-blur-md border border-white/10 px-4 py-3 animate-fade-up" style={{ animationDelay: "0.5s" }}>
              <ShieldCheck size={18} className="text-copper shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-bold text-ink leading-tight">{t(lang, "heroBadge1Title")}</p>
                <p className="text-xs text-subtle mt-0.5 leading-snug">{t(lang, "heroBadge1Sub")}</p>
              </div>
            </div>
            <div className="hidden sm:flex items-start gap-2.5 absolute bottom-5 right-5 rtl:right-auto rtl:left-5 max-w-[230px] rounded-xl bg-obsidian/70 backdrop-blur-md border border-white/10 px-4 py-3 animate-fade-up" style={{ animationDelay: "0.62s" }}>
              <Sparkles size={18} className="text-copper shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-bold text-ink leading-tight">{t(lang, "heroBadge2Title")}</p>
                <p className="text-xs text-subtle mt-0.5 leading-snug">{t(lang, "heroBadge2Sub")}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-10 sm:gap-16 mt-10 animate-fade-up" style={{ animationDelay: "0.4s" }}>
            {[["12+", t(lang, "services")], ["3", t(lang, "branches")], ["5.0", "★★★★★"]].map(([n, l]) => (
              <div key={l} className="text-center">
                <p className="text-3xl font-display font-extrabold text-copper">{n}</p>
                <p className="text-xs uppercase tracking-widest text-subtle mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES (Hizmetlerimiz) — slide in from sides ===== */}
      <section id="services" className="container-x py-20 md:py-28">
        <Reveal>
          <p className="eyebrow mb-3">01 — {t(lang, "services")}</p>
          <h2 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">{t(lang, "ourServices")}</h2>
          <div className="w-16 h-1 bg-copper mt-5 rounded-full" />
        </Reveal>

        <ServicesShowcase services={services} lang={lang} hrefFor={(slug) => withPrev(spath(lang, slug))} />
      </section>

      {/* ===== WHY US band ===== */}
      <section className="border-y border-white/10 bg-graphite/60">
        <div className="container-x py-16 md:py-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            [ShieldCheck, t(lang, "services"), page?.content?.sections?.[0]?.body || t(lang, "whyServicesFallback")],
            [Star, "5.0", t(lang, "whyRating")],
            [MapPin, t(lang, "branches"), t(lang, "whyBranches")],
          ].map(([Icon, title, body], i) => (
            <Reveal key={i} dir={i === 0 ? "left" : i === 2 ? "right" : "up"} delay={i * 110}>
              <div className="feature-card group card-surface h-full rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-copper/50 hover:shadow-[0_24px_60px_-24px_rgba(225,29,42,0.45)]">
                <div className="feat-bar h-1 w-12 bg-copper rounded-full mb-6" />
                <span className="feat-icon inline-flex w-14 h-14 rounded-2xl bg-copper/15 text-copper items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <Icon size={24} />
                </span>
                <p className="font-display text-2xl font-bold text-ink mt-5 group-hover:text-copper transition-colors duration-300">{title}</p>
                <p className="text-subtle text-sm mt-2 leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== GALLERY ===== */}
      <section id="gallery" className="container-x py-20 md:py-28">
        <Reveal>
          <p className="eyebrow mb-3">02 — {GALLERY_LABEL[lang] || GALLERY_LABEL.en}</p>
          <h2 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">{GALLERY_LABEL[lang] || GALLERY_LABEL.en}</h2>
          <div className="w-16 h-1 bg-copper mt-5 rounded-full" />
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-12" data-testid="home-gallery">
          {GALLERY.map((src, i) => (
            <Reveal key={i} dir={i % 2 === 0 ? "left" : "right"} delay={(i % 4) * 60}
              className={i % 5 === 0 ? "col-span-2 row-span-2" : ""}>
              <div className={`group relative rounded-xl overflow-hidden ${i % 5 === 0 ? "h-64 md:h-full" : "h-40 md:h-52"}`}>
                <img src={src} alt={`${page?.content?.h1 || "gallery"} ${i + 1}`} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-obsidian/20 group-hover:bg-obsidian/0 transition-colors" />
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <a
            href="https://www.instagram.com/sifirbirtuning"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-instagram"
            className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-white/15 text-sm text-ink hover:border-copper hover:text-copper transition-colors"
          >
            <Instagram size={18} className="text-copper" />
            {t(lang, "galleryInstagram")}
            <span className="font-semibold">@sifirbirtuning</span>
          </a>
        </div>
      </section>
      <section className="container-x pb-8 md:pb-16">
        <Reveal>
          <p className="eyebrow mb-3">03 — {t(lang, "branches")}</p>
          <h2 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink mb-12">{t(lang, "ourBranches")}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {branches.map((b, i) => (
            <Reveal key={b.entity_id} dir={i === 0 ? "left" : i === 2 ? "right" : "up"} delay={i * 110}>
              <div className="feature-card group card-surface rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:border-copper/50 hover:shadow-[0_24px_60px_-24px_rgba(225,29,42,0.45)]">
                <div className="p-8 flex flex-col flex-1">
                  <div className="feat-bar h-1 w-12 bg-copper rounded-full mb-6" />
                  <span className="feat-icon inline-flex w-12 h-12 rounded-xl bg-copper/15 text-copper items-center justify-center transition-transform duration-300 group-hover:scale-110">
                    <MapPin size={22} />
                  </span>
                  <Link to={withPrev(spath(lang, b.slug))} data-testid={`branch-card-${b.entity_id}`} className="mt-5">
                    <h3 className="text-xl font-bold text-ink group-hover:text-copper transition-colors duration-300">{b.title}</h3>
                  </Link>
                  {b.address && <p className="text-subtle text-sm mt-3 leading-relaxed flex-1" data-testid={`branch-card-address-${b.entity_id}`}>{b.address}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-4 text-sm text-subtle" data-testid={`branch-card-hours-${b.entity_id}`}>
                    <Clock size={15} className="text-copper" /> <span dir="ltr">{HOURS_LABEL}</span>
                    <OpenNowBadge />
                  </div>
                  <Link to={withPrev(spath(lang, b.slug))} className="inline-flex items-center gap-1.5 text-copper text-sm font-semibold mt-5 hover:gap-2.5 transition-all">
                    {t(lang, "contact")} <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FaqAccordion lang={lang} />

      {/* ===== QUOTE ===== */}
      <section id="quote" className="relative py-20 md:py-28 overflow-hidden">
        <img src={serviceImg("service_ceramic")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-obsidian/80" />
        <div className="relative container-x grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal dir="left">
            <p className="eyebrow mb-3">04 — {t(lang, "contact")}</p>
            <h2 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">{t(lang, "quoteTitle")}</h2>
            <p className="text-subtle mt-5 max-w-md leading-relaxed text-lg">{t(lang, "quoteSubtitle")}</p>
            <div className="w-16 h-1 bg-copper mt-6 rounded-full" />
            <div className="mt-8 space-y-4 max-w-md">
              <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-copper/15 text-copper group-hover:bg-copper group-hover:text-white transition-colors">
                  <Phone size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wide text-faint">{t(lang, "phone")}</span>
                  <span className="block text-ink text-lg font-semibold group-hover:text-copper transition-colors" dir="ltr">{CONTACT_PHONE}</span>
                </span>
              </a>
              <a href={`https://wa.me/${CONTACT_WHATSAPP}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-copper/15 text-copper group-hover:bg-copper group-hover:text-white transition-colors">
                  <MessageCircle size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wide text-faint">WhatsApp</span>
                  <span className="block text-ink text-lg font-semibold group-hover:text-copper transition-colors" dir="ltr">{CONTACT_PHONE}</span>
                </span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-hours">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-copper/15 text-copper">
                  <Clock size={18} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wide text-faint">{t(lang, "hours")}</span>
                  <span className="flex items-center gap-2 mt-0.5">
                    <span className="text-ink text-lg font-semibold" dir="ltr">{HOURS_LABEL}</span>
                    <OpenNowBadge />
                  </span>
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal dir="right"><LeadForm /></Reveal>
        </div>
      </section>
    </div>
  );
}
